/**
 * UniqueRandomGenerator is a class that generates random integers in a range,
 * excluding the numbers that have been generated before, while in the randomized buffer.
 */
export class UniqueRandomGenerator {
  private min: number = 0;
  private max: number = 0;
  private buffer: number[] = [];
  private bufferSize: number = 0;
  private bufferSizeSet: boolean = false;
  private range: number = 0;

  /**
   * Sets the buffer size for the generator.
   * @param bufferSize - The size of the buffer.
   * @returns The instance of UniqueRandomGenerator for method chaining.
   */
  setBufferSize(bufferSize: number): this {
    if (this.bufferSize !== bufferSize) {
      this.bufferSize = bufferSize;
      this.bufferSizeSet = true;
    }
    return this;
  }

  /**
   * Sets the minimum value for the range.
   * @param min - The minimum value.
   * @returns The instance of UniqueRandomGenerator for method chaining.
   */
  setMin(min: number): this {
    if (this.min !== min) {
      this.min = min;
      this.updateRange();
    }
    return this;
  }

  /**
   * Sets the maximum value for the range.
   * @param max - The maximum value.
   * @returns The instance of UniqueRandomGenerator for method chaining.
   */
  setMax(max: number): this {
    if (this.max !== max) {
      this.max = max;
      this.updateRange();
    }
    return this;
  }

  /**
   * Sets both the minimum and maximum values for the range.
   * @param min - The minimum value.
   * @param max - The maximum value.
   * @returns The instance of UniqueRandomGenerator for method chaining.
   */
  setMinMax(min: number, max: number): this {
    this.setMin(min);
    this.setMax(max);
    return this;
  }

  /**
   * Updates the range based on the current min and max values.
   */
  private updateRange(): void {
    if (this.min !== null && this.max !== null) {
      this.range = this.max - this.min + 1;
      if (!this.bufferSizeSet) {
        this.updateBufferSize();
        this.bufferSizeSet = true;
      }
    }
  }

  /**
   * Set the buffer size to a random number between 0 and this.range - 1,
   * so that the buffer size changes every time it's exhausted.
   */
  private updateBufferSize(): void {
    this.bufferSize = Math.floor(Math.random() * (this.range - 1));
  }

  /**
   * Generates a cryptographically secure random integer within the specified range.
   * This function ensures that the generated number hasn't been recently produced
   * by maintaining a buffer of previously generated numbers.
   *
   * @returns A random integer between `this.min` (inclusive) and
   *          `this.min + this.range - 1` (inclusive).
   * @throws Error if min or max are not set.
   */
  getRandomIntegerInRange(min: number = 0, max: number = 0): number {
    if (min > 0 && max > 0) {
      this.min = min;
      this.max = max;
      this.updateRange();
    }

    if (this.min === this.max) {
      return this.min; // Return the min value if the range is a single number
    }

    if (this.min === null || this.max === null) {
      throw new Error(
        'Min and max values must be set before generating random numbers.'
      );
    }

    const bitSize = Math.ceil(Math.log2(this.range));
    const byteSize = Math.ceil(bitSize / 8);
    let randomBytes: Uint8Array;
    let randomNumber: number;

    do {
      randomBytes = new Uint8Array(byteSize);
      window.crypto.getRandomValues(randomBytes);
      randomNumber = 0;
      for (let i = 0; i < byteSize; i++) {
        randomNumber |= randomBytes[i] << (8 * i);
      }
      randomNumber = (randomNumber % this.range) + this.min;
    } while (
      this.buffer.includes(randomNumber) &&
      this.buffer.length < this.range - 1
    );

    if (this.buffer.length >= this.bufferSize) {
      this.buffer.shift();
      // Update bufferSize after the buffer is reset
      this.updateBufferSize();
    }
    this.buffer.push(randomNumber);

    return randomNumber;
  }
}
